import React from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import history from 'app/routes/history';
import HeaderThin from 'app/components/layout/HeaderThin';
import SectionGroup from 'app/components/general/SectionGroup';
import SchemaForm from 'app/components/general/SchemaForm';

import * as connectionActions from 'app/ducks/connections/actions';
import * as loadingSelectors from 'app/ducks/loading/selectors';
import * as connectionSelectors from 'app/ducks/connections/selectors';
import * as organisationSelectors from 'app/ducks/organisations/selectors';
import * as competitionSelectors from 'app/ducks/competitions/selectors';
import * as connectionTypeActions from 'app/ducks/connectionTypes/actions';
import * as connectionTypeSelectors from 'app/ducks/connectionTypes/selectors';

interface IConnectionCreateProps {
  appReady: boolean;
  actions: any;
  match: any;
  connections: any;
  organisations: any;
  competitions: any;
  connectionTypes: any;
  errors: any;
}

interface IConnectionCreateState {
  originalFormData: any;
  formData: any;
  changed: boolean;
  saved: boolean;
  errors: any;
}

const selector = (state): any => ({
  connections: connectionSelectors.getConnections(state),
  organisations: organisationSelectors.getOrganisations(state),
  competitions: competitionSelectors.getCompetitions(state),
  connectionTypes: connectionTypeSelectors.getConnectionTypes(state),
  appReady: loadingSelectors.appReady(state),
  connect: state.connect,
  authentication: state.authentication,
  errors: {
    ...state.connections.backendErrors,
  },
});

const actionCreators = _.merge(connectionActions, connectionTypeActions);

@connect(selector, (dispatch) => ({
  actions: bindActionCreators(actionCreators, dispatch),
}))
export default class ConnectionCreate extends React.Component<
IConnectionCreateProps,
IConnectionCreateState
> {
  static defaultProps: IConnectionCreateProps = {
    appReady: false,
    actions: undefined,
    match: undefined,
    connections: undefined,
    organisations: undefined,
    competitions: undefined,
    connectionTypes: undefined,
    errors: undefined,
  };

  state: IConnectionCreateState = {
    originalFormData: {},
    formData: {
      connectionType: undefined,
      metadata: undefined,
    },
    changed: false,
    saved: false,
    errors: {},
  };

  componentDidMount = () => {
    this.props.actions.getAllConnectionTypes();
  };

  static getDerivedStateFromProps(nextProps, prevState): any {
    if (!prevState.changed) {
      if (nextProps.match?.params?.connectionType === 'gameday') {
        const connectionType = _.find(nextProps.connectionTypes.connectionTypesById, { code: 'gameday' });
        if (connectionType !== undefined && !_.isEmpty(nextProps.connect?.prefilledInformation?.gamedayOrganisationId)) {
          return {
            ...prevState,
            changed: true,
            formData: {
              ...prevState.formData,
              connectionType,
              metadata: {
                gamedayId: nextProps.connect?.prefilledInformation?.gamedayOrganisationId,
              },
            },
          };
        }
      }
      return {
        ...prevState,
        errors: nextProps.errors,
      };
    }
    return null;
  }

  isValid = (): boolean => _.isEmpty(this.state.errors) && this.state.changed;

  handleChange = (e): void => {
    const changed = this.state.originalFormData !== e.formData;
    this.setState(() => ({
      formData: e.formData,
      errors: e.errors,
      changed,
    }));
  };

  handleError = (errors): void => {
    this.setState({ errors });
  }

  handleCancel = (e): void => {
    // e.preventDefault();
    history.push('/connections');
  }

  handleSubmit = (e): void => {
    // e.preventDefault();
    if (this.isValid()) {
      const connectionType = this.props.connectionTypes.connectionTypesById[this.state.formData.connectionType.id];

      const payload = {
        name: connectionType.name,
        code: connectionType.code,
        version: connectionType.version,
        description: connectionType.description,
        metadata: this.state.formData.metadata,
        connectionType: !_.isEmpty(this.state.formData.connectionType)
          ? { id: this.state.formData.connectionType.id }
          : null,
        organisation: {
          id: this.props.organisations.currentOrganisationId,
        },
      };

      this.props.actions.createConnection(payload);
    }
    this.setState({ changed: false });
  };

  render(): React.ReactNode {
    const metadataSchema = this.props.connectionTypes.connectionTypesById[
      this.state?.formData?.connectionType?.id
    ]?.metadataSchema;
    const connectionSchema = !_.isEmpty(this.state.formData.connectionType)
      ? metadataSchema
      : {};
    const connectionUiSchema = !_.isEmpty(this.state.formData.connectionType)
      ? {
        metadata: this.props.connectionTypes.connectionTypesById[
          this.state.formData.connectionType.id
        ].metadataUiSchema,
      }
      : {};
    const formSchema = {
      required: ['connectionType'],
      properties: {
        connectionType: {
          type: 'object',
          title: 'Type',
          options: connectionTypeSelectors.getConnectionTypesByIdInSelectFormat(
            this.props.connectionTypes,
          ),
          inputType: 'select',
        },
      },
    };
    if (!_.isEmpty(connectionSchema)) {
      formSchema.properties.metadata = {
        type: 'object',
        title: 'Configuration',
        properties: connectionSchema,
      };
    }
    const uiSchema = {
      root: { classNames: 'row' },
      connectionType: { 'ui:field': 'select', classNames: 'col col-12' },
      metadata: { classNames: 'col col-12', root: { classNames: 'row' } },
    };
    return (
      <div>
        <div className="row">
          <div className="col">
            <HeaderThin
              title="Create Connection"
              divider
            />
          </div>
        </div>
        <div className="row">
          <div className="col">
            <SectionGroup title="Connection Details">
              <SchemaForm
                uiSchema={uiSchema}
                schema={formSchema}
                handleChange={this.handleChange}
                handleError={this.handleError}
                handleSubmit={this.handleSubmit}
                handleCancel={this.handleCancel}
                formData={this.state.formData}
                errors={this.props.errors}
                liveValidate={false}
                isValid={this.isValid()}
                isLoading={this.props.connections.isSaving}
                hasSaved={this.props.connections.hasSaved}
                hasFailed={this.props.connections.hasFailed}
                originalFormData={this.state.originalFormData}
                buttonText="Create Connection"
              />
            </SectionGroup>
          </div>
        </div>
      </div>
    );
  }
}
